import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { Plus, Pencil, Trash2, Tag, ListChecks, Target } from 'lucide-react'
import {
  Card,
  CardContent,
  Button,
  Input,
  Label,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@jira-clone/ui'
import {
  useLeadSources,
  useLeadStatuses,
  useDealStages,
  useLeadSourceMutations,
  useLeadStatusMutations,
  useDealStageMutations,
} from '../../shared/hooks/useCrmVocabulary'
import { getApiErrorMessage } from '../../shared/utils/apiError'

const DEFAULT_COLOR = '#6366f1'

type Kind = 'lead-sources' | 'lead-statuses' | 'deal-stages'

const TABS: { key: Kind; label: string; icon: React.ReactNode }[] = [
  { key: 'lead-sources', label: 'Lead Sources', icon: <Tag className="h-4 w-4" /> },
  { key: 'lead-statuses', label: 'Lead Statuses', icon: <ListChecks className="h-4 w-4" /> },
  { key: 'deal-stages', label: 'Deal Stages', icon: <Target className="h-4 w-4" /> },
]

type DraftState = {
  id?: string
  name: string
  color?: string
  isWon?: boolean
  isLost?: boolean
  defaultProbability?: string
}

export default function CrmVocabularyAdminPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab') as Kind | null
  const initialTab: Kind = tabParam && ['lead-sources', 'lead-statuses', 'deal-stages'].includes(tabParam)
    ? tabParam
    : 'lead-sources'
  const [activeTab, setActiveTabState] = useState<Kind>(initialTab)

  useEffect(() => {
    if (tabParam && ['lead-sources', 'lead-statuses', 'deal-stages'].includes(tabParam)) {
      setActiveTabState(tabParam)
    } else if (!tabParam) {
      setActiveTabState('lead-sources')
    }
  }, [tabParam])

  const handleTabChange = (tab: Kind) => {
    setActiveTabState(tab)
    setSearchParams(tab === 'lead-sources' ? {} : { tab })
  }

  const leadSources = useLeadSources()
  const leadStatuses = useLeadStatuses()
  const dealStages = useDealStages()
  const leadSourceM = useLeadSourceMutations()
  const leadStatusM = useLeadStatusMutations()
  const dealStageM = useDealStageMutations()

  const query = activeTab === 'lead-sources' ? leadSources : activeTab === 'lead-statuses' ? leadStatuses : dealStages
  const mutations = activeTab === 'lead-sources' ? leadSourceM : activeTab === 'lead-statuses' ? leadStatusM : dealStageM
  const rows = (query.data || []) as any[]

  const [dialogOpen, setDialogOpen] = useState(false)
  const [draft, setDraft] = useState<DraftState>(() => ({
    name: '', color: '#6366f1', isWon: false, isLost: false, defaultProbability: ''
  }))
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const openCreate = () => {
    setDraft({ name: '', color: '#6366f1', isWon: false, isLost: false, defaultProbability: '' })
    setDialogOpen(true)
  }

  const openEdit = (row: any) => {
    setDraft({
      id: row.id,
      name: row.name,
      color: row.color || '#6366f1',
      isWon: !!row.isWon,
      isLost: !!row.isLost,
      defaultProbability: row.defaultProbability != null ? String(row.defaultProbability) : '',
    })
    setDialogOpen(true)
  }

  const handleSave = async () => {
    if (!draft.name.trim()) { toast.error('Name is required.'); return }
    const payload: Record<string, any> = { name: draft.name.trim(), color: draft.color || DEFAULT_COLOR }
    if (activeTab !== 'lead-sources') {
      payload.isWon = draft.isWon
      payload.isLost = draft.isLost
    }
    if (activeTab === 'deal-stages' && draft.defaultProbability !== '') {
      payload.defaultProbability = Math.min(100, Math.max(0, Number(draft.defaultProbability) || 0))
    }
    try {
      if (draft.id) {
        await mutations.update.mutateAsync({ id: draft.id, payload })
        toast.success('Updated successfully.')
      } else {
        await mutations.create.mutateAsync(payload)
        toast.success('Created successfully.')
      }
      setDialogOpen(false)
    } catch (err) {
      toast.error(getApiErrorMessage(err, draft.id ? 'update this row' : 'create this row'))
    }
  }

  const handleDelete = async (row: any) => {
    if (!confirm(`Delete "${row.name}"? This can't be undone.`)) return
    setDeletingId(row.id)
    try {
      await mutations.remove.mutateAsync(row.id)
      toast.success('Deleted.')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'delete this row'))
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-foreground">CRM Vocabulary</h2>
          <p className="text-sm text-muted-foreground">Manage lead sources, lead statuses, and deal stages.</p>
        </div>
        <Button onClick={openCreate} className="gap-1.5">
          <Plus className="h-4 w-4" /> New {(TABS.find((t) => t.key === activeTab)?.label || '').replace(/e?s$/, '')}
        </Button>
      </div>

      <div className="flex items-center gap-1 border-b border-border">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => handleTabChange(tab.key)}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === tab.key
                ? 'border-primary text-primary font-semibold'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {query.isLoading ? (
        <p className="text-sm text-muted-foreground py-8">Loading...</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8">
          No {TABS.find((t) => t.key === activeTab)?.label.toLowerCase() || ''} yet. Create the first one.
        </p>
      ) : (
        <div className="space-y-2">
          {rows.map((row) => (
            <Card key={row.id}>
              <CardContent className="p-4 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="h-3 w-3 rounded-full shrink-0" style={{ backgroundColor: row.color || '#6366f1' }} />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-foreground truncate">{row.name}</p>
                    <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                      {row.isWon && <span className="text-emerald-600 dark:text-emerald-400 font-medium">Won</span>}
                      {row.isLost && <span className="text-destructive font-medium">Lost</span>}
                      {activeTab === 'deal-stages' && row.defaultProbability != null && <span>{row.defaultProbability}% probability</span>}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(row)} title="Edit">
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(row)} disabled={deletingId === row.id} title="Delete">
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {draft.id ? 'Edit' : 'New'} {['Lead Source', 'Lead Status', 'Deal Stage'][['lead-sources', 'lead-statuses', 'deal-stages'].indexOf(activeTab)]}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label htmlFor="cv-name">Name</Label>
              <Input
                id="cv-name"
                value={draft.name}
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              />
            </div>
            {activeTab !== 'lead-sources' && (
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={draft.isWon} onChange={(e) => setDraft((d) => ({ ...d, isWon: e.target.checked }))} /> Marks as Won
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={draft.isLost} onChange={(e) => setDraft((d) => ({ ...d, isLost: e.target.checked }))} /> Marks as Lost
                </label>
              </div>
            )}
            {activeTab === 'deal-stages' && (
              <div className="space-y-1.5">
                <Label htmlFor="cv-probability">Default Probability (%)</Label>
                <Input
                  id="cv-probability"
                  type="number"
                  min={0}
                  max={100}
                  value={draft.defaultProbability}
                  onChange={(e) => setDraft((d) => ({ ...d, defaultProbability: e.target.value }))}
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="cv-color">Color</Label>
              <div className="flex items-center gap-2">
                <input
                  id="cv-color"
                  type="color"
                  value={draft.color}
                  onChange={(e) => setDraft((d) => ({ ...d, color: e.target.value }))}
                  className="h-9 w-12 rounded border border-border cursor-pointer"
                />
                <span className="text-xs text-muted-foreground font-mono">{draft.color}</span>
              </div>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={mutations.create.isPending || mutations.update.isPending}>
              {draft.id ? 'Save Changes' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}